import Input from "components/Common/Input";
import Copyright from "components/Copyright";
import PageMeta from "components/PageMeta";
import React, { FormEvent, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Alert, Card, Col, Container, Row, Spinner } from "reactstrap";
import { resetCustomerPassword } from "services/AccountApi";
import { ThemeLogo, useTheme } from "theme";

const CustomerResetPassword: React.FC = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [token] = useState(() => searchParams.get("token") ?? "");
    const [password, setPassword] = useState("");
    const [confirmation, setConfirmation] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [changed, setChanged] = useState(false);
    const [error, setError] = useState(false);

    const passwordsMatch = password === confirmation;
    const validPassword = password.length >= 8 && password.length <= 72;

    const submit = async (event: FormEvent) => {
        event.preventDefault();
        if (!token || !validPassword || !passwordsMatch || submitting) return;

        setSubmitting(true);
        setError(false);
        try {
            await resetCustomerPassword(token, password);
            setChanged(true);
            navigate("/reset-password", { replace: true });
        } catch {
            setError(true);
        } finally {
            setSubmitting(false);
        }
    };

    return <>
        <PageMeta title={`Set new password | ${theme.brand.companyName}`} hideTitle />
        <div className="account-pages my-0 pt-sm-5">
            <Container>
                <Row className="justify-content-center">
                    <Col md={10} className="text-center mt-3">
                        <ThemeLogo variant="dark" className="customer-login-logo" />
                        <div className="text-center login-height" />
                        <Row>
                            <Col lg={3} />
                            <Col lg={6}>
                                <Card className="overflow-hidden">
                                    <div className="p-lg-5 p-4">
                                        <h5>Set a new password</h5>
                                        {changed ? <>
                                            <Alert color="success">Your password has been changed.</Alert>
                                            <Link className="btn btn-primary w-100" to="/login">Continue to login</Link>
                                        </> : !token ? <>
                                            <Alert color="danger">This reset link is invalid or incomplete.</Alert>
                                            <Link to="/forgot-password">Request a new link</Link>
                                        </> : <form onSubmit={submit}>
                                            <p className="text-muted mb-4">Choose a password of at least 8 characters.</p>
                                            <Input
                                                autoFocus
                                                autocomplete="new-password"
                                                name="password"
                                                type="password"
                                                placeholder="New password"
                                                value={password}
                                                onChange={setPassword}
                                            />
                                            <br />
                                            <Input
                                                autocomplete="new-password"
                                                name="password_confirmation"
                                                type="password"
                                                placeholder="Repeat new password"
                                                value={confirmation}
                                                onChange={setConfirmation}
                                            />
                                            {!passwordsMatch && confirmation !== "" && <Alert className="mt-4" color="warning">
                                                The passwords do not match.
                                            </Alert>}
                                            {error && <Alert className="mt-4" color="danger">
                                                This reset link is invalid or has expired. Request a new link and try again.
                                            </Alert>}
                                            <button
                                                className="btn btn-primary w-100 mt-4 waves-effect waves-light"
                                                disabled={!validPassword || !passwordsMatch || submitting}
                                                type="submit"
                                            >
                                                {submitting && <Spinner size="sm" className="me-2" />}
                                                Save new password
                                            </button>
                                        </form>}
                                    </div>
                                </Card>
                                <div className="text-center"><Copyright /></div>
                            </Col>
                        </Row>
                    </Col>
                </Row>
            </Container>
        </div>
    </>;
};

export default CustomerResetPassword;
